/*!
 * stageMask.js — mask: / hole: -- a shape as a window onto something drawn
 * © 2026 Rob Canning — GPLv3
 *
 *     text(uid:w, data:hex, length:400, mask:spot)   the text only inside the shape spot
 *     text(uid:w, ..., hole:spot)                     everywhere but inside it
 *     cam(eye, mask:star)   viz(bass, mask:spot)   screen(mask:spot)   the camera, a scope,
 *                                                     the whole stage, through it
 *     ... mask:off / hole:off                         whole again
 *     cam(eye, mask:w)  viz(noise, view:barcode, mask:w)   a TEXT as the window: the camera,
 *                                                     a barcode, seen only in the letters of w
 *                                                     (the text itself hidden while it is one)
 *
 * The shape can be anything shape() makes -- moving (at:sinOsc), turning
 * (rotate), a cluster (several windows) -- and needs not be seen
 * (opacity:0). Each frame its outline is read on the screen and laid on what
 * it masks as a CSS clip-path (path(), in that element's own px): HTML text,
 * the canvases a camera or a scope paints, the stage's container. The
 * outline is sampled from the path data in JS (the browser's
 * getPointAtLength costs ~0.1 ms a point) and again only when the data
 * changes; a text window's letters are drawn once for where they stand
 * against each other and moved with mask-position. Pure maths (samplePath,
 * outlinePath) apart; the rest reads the page.
 */

import { onReset } from "./teardown.js";

const masks = new Map();            // key -> { targets: () => [el], shape, hole, goneSince }
let raf = 0;
const SAMPLES = 64;                 // points round each outline
const GONE_MS = 2000;               // a mask whose window and targets are both gone this long is dropped

// ---- an outline from path data ----------------------------------------------------------

/** An arc (SVG's A, endpoint form) from (x1, y1) as short lines: the end of each pushed onto `out`. */
function arcPoints(x1, y1, rx, ry, rot, large, sweep, x2, y2, out) {
  if (x1 === x2 && y1 === y2) return;
  rx = Math.abs(rx); ry = Math.abs(ry);
  if (!rx || !ry) { out.push([x2, y2]); return; }
  const phi = (rot * Math.PI) / 180, cos = Math.cos(phi), sin = Math.sin(phi);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const xp = cos * dx + sin * dy, yp = -sin * dx + cos * dy;
  // radii too small to reach: scaled up until they just do (SVG F.6.6)
  const lam = (xp * xp) / (rx * rx) + (yp * yp) / (ry * ry);
  if (lam > 1) { const s = Math.sqrt(lam); rx *= s; ry *= s; }
  const num = rx * rx * ry * ry - rx * rx * yp * yp - ry * ry * xp * xp;
  const den = rx * rx * yp * yp + ry * ry * xp * xp;
  const co = (large === sweep ? -1 : 1) * Math.sqrt(Math.max(0, num / den));
  const cxp = (co * rx * yp) / ry, cyp = (-co * ry * xp) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2, cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const ang = (ux, uy, vx, vy) => Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
  const ux = (xp - cxp) / rx, uy = (yp - cyp) / ry;
  const t1 = ang(1, 0, ux, uy);
  let dt = ang(ux, uy, (-xp - cxp) / rx, (-yp - cyp) / ry);
  if (!sweep && dt > 0) dt -= 2 * Math.PI;
  else if (sweep && dt < 0) dt += 2 * Math.PI;
  // a line each 1/128 of a turn: under 0.1 px off a 400 px circle
  const n = Math.max(1, Math.ceil(Math.abs(dt) / (Math.PI / 64)));
  for (let k = 1; k <= n; k++) {
    const t = t1 + (dt * k) / n, ct = Math.cos(t), st = Math.sin(t);
    out.push(k === n ? [x2, y2] : [cx + rx * ct * cos - ry * st * sin, cy + rx * ct * sin + ry * st * cos]);
  }
}

/**
 * `n` points evenly along path data `d` (as getPointAtLength at i/n of the
 * whole length would give them), in the path's own units: [[x, y] ...]; []
 * for a path of no length; null for data this does not read (anything but
 * absolute M L H V A Z).
 */
export function samplePath(d, n = SAMPLES) {
  const tok = String(d).match(/[a-zA-Z]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g) || [];
  const runs = [];                  // the lines drawn, one list of points for each subpath
  let run = null, x = 0, y = 0, x0 = 0, y0 = 0, cmd = "";
  for (let i = 0; i < tok.length;) {
    if (/[a-zA-Z]/.test(tok[i])) cmd = tok[i++];
    const num = k => { const v = Number(tok[i + k]); return Number.isFinite(v) ? v : NaN; };
    if (cmd === "Z") { if (run) { run.push([x0, y0]); x = x0; y = y0; run = null; } cmd = ""; continue; }
    const need = { M: 2, L: 2, H: 1, V: 1, A: 7 }[cmd];
    if (!need) return null;
    const v = Array.from({ length: need }, (_, k) => num(k));
    // an arc's flags are 0 or 1 ("011" run together is not read)
    if (v.some(Number.isNaN) || (cmd === "A" && !(/^[01]$/.test(v[3]) && /^[01]$/.test(v[4])))) return null;
    i += need;
    if (cmd === "M") { [x, y] = v; x0 = x; y0 = y; runs.push(run = [[x, y]]); cmd = "L"; continue; }
    if (!run) runs.push(run = [[x, y]]);
    if (cmd === "L") [x, y] = v;
    else if (cmd === "H") x = v[0];
    else if (cmd === "V") y = v[0];
    else { arcPoints(x, y, v[0], v[1], v[2], !!v[3], !!v[4], v[5], v[6], run); [x, y] = [v[5], v[6]]; continue; }
    run.push([x, y]);
  }
  // the whole length, and n points at even steps along it (a move between subpaths has none)
  const segs = [];
  let total = 0;
  for (const r of runs) for (let k = 1; k < r.length; k++) {
    const len = Math.hypot(r[k][0] - r[k - 1][0], r[k][1] - r[k - 1][1]);
    if (len > 0) { segs.push([r[k - 1], r[k], total, len]); total += len; }
  }
  if (!(total > 0)) return [];
  const out = [];
  let s = 0;
  for (let i = 0; i < n; i++) {
    const at = (i / n) * total;
    while (s < segs.length - 1 && segs[s][2] + segs[s][3] < at) s++;
    const [a, b, from, len] = segs[s], t = Math.min(1, Math.max(0, (at - from) / len));
    out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
  }
  return out;
}

const sampled = new WeakMap();      // a body -> { d, pts }: its outline in its own units for that path data

/** A body's outline in its own units: from its path data, the browser asked only for data samplePath does not read. */
function bodyPoints(p) {
  const d = p.getAttribute("d") ?? "";
  const had = sampled.get(p);
  if (had?.d === d) return had.pts;
  let pts = samplePath(d);
  if (pts == null) {
    let len = 0;
    try { len = p.getTotalLength(); } catch { /* not drawn */ }
    pts = [];
    for (let i = 0; len > 0 && i < SAMPLES; i++) { const q = p.getPointAtLength((i / SAMPLES) * len); pts.push([q.x, q.y]); }
  }
  sampled.set(p, { d, pts });
  return pts;
}

/** A shape's outlines on the screen: one list of [x, y] for each of its bodies (a cluster: each member's). */
export function outlinesOf(uid) {
  const el = document.getElementById(uid);
  if (!el) return [];
  const bodies = el.matches?.("path.oscilla-shape-body") ? [el] : [...el.querySelectorAll("path.oscilla-shape-body")];
  const out = [];
  for (const p of bodies) {
    const m = p.getScreenCTM?.();
    const pts = m ? bodyPoints(p) : null;
    if (!pts?.length) continue;
    out.push(pts.map(([x, y]) => [m.a * x + m.c * y + m.e, m.b * x + m.d * y + m.f]));
  }
  return out;
}

/** Is [x, y] inside the outline (pts: [[x, y] ...], closed)? Even-odd. */
export function insideOutline(pts, x, y) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Where a level line at y crosses an outline: [left, right] of the crossings, null if it misses. */
export function chordAt(pts, y) {
  let a = Infinity, b = -Infinity;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > y) === (yj > y)) continue;
    const x = xi + (y - yi) * (xj - xi) / (yj - yi);
    a = Math.min(a, x); b = Math.max(b, x);
  }
  return a <= b ? [a, b] : null;
}

/** An outline's box on the screen: { x0, y0, x1, y1 }; null for none. */
export function outlineBox(pts) {
  if (!pts?.length) return null;
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/** A point somewhere inside an outline, picked at random ([x, y]); null for no outline. */
export function randomPointIn(pts, rnd = Math.random) {
  const box = outlineBox(pts);
  if (!box) return null;
  const { x0, y0, x1, y1 } = box;
  for (let k = 0; k < 64; k++) {
    const x = x0 + rnd() * (x1 - x0), y = y0 + rnd() * (y1 - y0);
    if (insideOutline(pts, x, y)) return [x, y];
  }
  return [(x0 + x1) / 2, (y0 + y1) / 2];
}

/**
 * The clip-path for an element at `rect` (its box on the screen) whose own
 * size is w x h px: the outlines moved into its px; a hole is the element's
 * whole box with them cut out (evenodd).
 */
export function outlinePath(outlines, rect, w, h, hole = false) {
  const kx = rect.width ? w / rect.width : 1, ky = rect.height ? h / rect.height : 1;
  const f = n => +n.toFixed(1);
  const sub = outlines.map(pts => "M " + pts.map(([x, y]) => `${f((x - rect.left) * kx)} ${f((y - rect.top) * ky)}`).join(" L ") + " Z").join(" ");
  if (hole) return `path(evenodd, "M -1 -1 H ${f(w + 1)} V ${f(h + 1)} H -1 Z ${sub}")`;
  // no outline at all (the shape not there yet): nothing shows through a mask
  return sub ? `path("${sub}")` : 'path("M 0 0 Z")';
}

// ---- a text as the window: its letters, drawn on a canvas, as a mask-image --------------

/** A text's lines on the stage (text.js overlays: data-uid), or none. */
const textEls = uid => [...document.querySelectorAll(`[data-uid="${CSS.escape(uid)}"]`)].filter(e => e.tagName !== "rect" && !e.closest("svg"));

const letterMasks = new Map();     // uid -> { key, url, w, h, dx, dy }

/**
 * The letters of the text's lines `els` as a mask image: each character drawn
 * where it stands (a Range on it), in its own font, white on clear, on a
 * canvas the size of the lines' box and a font size round it (for what sticks
 * out). Drawn again only when the text, its font or where its lines stand
 * against each other changes: the text moved whole is the same image, placed
 * anew (drawn on whole px, so a still text's letters are as sharp as drawn).
 * { url, x, y, w, h }: the image and its box on the screen now.
 */
function letterMask(uid, els) {
  const rects = els.map(el => el.getBoundingClientRect());
  const x0 = Math.min(...rects.map(r => r.left)), y0 = Math.min(...rects.map(r => r.top));
  const x1 = Math.max(...rects.map(r => r.right)), y1 = Math.max(...rects.map(r => r.bottom));
  const parts = [];
  let pad = 0;
  els.forEach((el, k) => {
    const cs = getComputedStyle(el), r = rects[k];
    pad = Math.max(pad, parseFloat(cs.fontSize) || 16);
    parts.push(el.textContent, Math.round(r.left - x0), Math.round(r.top - y0), Math.round(r.width), Math.round(r.height), cs.font);
  });
  pad = Math.ceil(pad);
  const key = parts.join("|");
  const had = letterMasks.get(uid);
  if (had?.key === key) return { url: had.url, x: x0 - had.dx, y: y0 - had.dy, w: had.w, h: had.h };
  const x = Math.floor(x0) - pad, y = Math.floor(y0) - pad;
  const cv = document.createElement("canvas");
  cv.width = Math.max(1, Math.ceil(x1 - x) + pad); cv.height = Math.max(1, Math.ceil(y1 - y) + pad);
  const g = cv.getContext("2d");
  g.fillStyle = "#fff";
  g.textBaseline = "middle";
  const range = document.createRange();
  for (const el of els) {
    const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let node = walk.nextNode(); node; node = walk.nextNode()) {
      g.font = getComputedStyle(node.parentElement).font;
      const t = node.textContent;
      for (let i = 0; i < t.length; i++) {
        if (!t[i].trim()) continue;
        range.setStart(node, i); range.setEnd(node, i + 1);
        const rc = range.getClientRects()[0];
        if (rc) g.fillText(t[i], rc.left - x, rc.top + rc.height / 2 - y);
      }
    }
  }
  const lm = { key, url: cv.toDataURL(), w: cv.width, h: cv.height, dx: x0 - x, dy: y0 - y };
  letterMasks.set(uid, lm);
  return { url: lm.url, x, y, w: lm.w, h: lm.h };
}

/**
 * The letters' mask `lm` on an element whose box is `r` on the screen and w x h
 * px of its own; a hole: a whole layer with the letters taken out of it
 * (subtract; exclude draws the letters' edges softer). Each part written only when it changes.
 */
function setLetterMask(el, lm, r, w, h, hole) {
  const kx = r.width ? w / r.width : 1, ky = r.height ? h / r.height : 1;
  const s = el.style, was = el._oscillaLetters;
  const url = lm?.url ?? null;
  if (was?.url !== url || was.hole !== hole) {
    const img = url ? `url("${url}")` : "linear-gradient(transparent, transparent)";
    s.maskImage = s.webkitMaskImage = hole ? `linear-gradient(#fff, #fff), ${img}` : img;
    s.maskComposite = hole ? "subtract" : "";
    s.maskRepeat = s.webkitMaskRepeat = "no-repeat";
  }
  const size = lm ? `${lm.w * kx}px ${lm.h * ky}px` : "100% 100%";
  const pos = lm ? `${(lm.x - r.left) * kx}px ${(lm.y - r.top) * ky}px` : "0px 0px";
  if (was?.size !== size || was.hole !== hole) s.maskSize = s.webkitMaskSize = hole ? `100% 100%, ${size}` : size;
  if (was?.pos !== pos || was.hole !== hole) s.maskPosition = s.webkitMaskPosition = hole ? `0px 0px, ${pos}` : pos;
  el._oscillaLetters = { url, hole, size, pos };
}

function unmaskEl(el) {
  const s = el.style;
  s.clipPath = "";
  s.maskImage = s.webkitMaskImage = s.maskSize = s.webkitMaskSize = s.maskPosition = s.webkitMaskPosition = s.maskRepeat = s.webkitMaskRepeat = s.maskComposite = "";
  el._oscillaMasked = false;
  el._oscillaClip = el._oscillaLetters = null;
}

/** Is the window a shape (its outline) or a text (its letters)? */
const isShape = el => !!el?.classList?.contains("oscilla-shape") || !!el?.classList?.contains("oscilla-shape-cluster");

function frame() {
  raf = 0;
  if (!masks.size) return;
  const now = performance.now();
  const outlines = new Map(), letters = new Map();      // each window read once a frame
  for (const [key, m] of masks) {
    const win = document.getElementById(m.shape);
    const lines = isShape(win) ? [] : (letters.get(m.shape) ?? letters.set(m.shape, textEls(m.shape)).get(m.shape));
    const targets = [...m.targets()];
    // the window and all it masked gone a while (still()'d, the lines run out): dropped
    if (!win && !lines.length && !targets.some(el => el.isConnected)) {
      m.goneSince ??= now;
      if (now - m.goneSince > GONE_MS) masks.delete(key);
      continue;
    }
    m.goneSince = null;
    if (lines.length) {
      const lm = letterMask(m.shape, lines);
      for (const el of lines) { if (el.style.visibility !== "hidden") { el._oscillaWasVisibility = el.style.visibility; el.style.visibility = "hidden"; } }
      for (const el of targets) {
        const r = el.getBoundingClientRect();
        if (!r.width && !r.height) continue;
        setLetterMask(el, lm, r, el.offsetWidth || el.clientWidth || r.width, el.offsetHeight || el.clientHeight || r.height, m.hole);
        el._oscillaMasked = true;
      }
      continue;
    }
    const shapes = outlines.get(m.shape) ?? outlines.set(m.shape, outlinesOf(m.shape)).get(m.shape);
    for (const el of targets) {
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) continue;
      const w = el.offsetWidth || el.clientWidth || r.width, h = el.offsetHeight || el.clientHeight || r.height;
      const clip = outlinePath(shapes, r, w, h, m.hole);
      if (el._oscillaClip !== clip) el.style.clipPath = el._oscillaClip = clip;
      el._oscillaMasked = true;
    }
  }
  raf = requestAnimationFrame(frame);
}

/**
 * What `key` masks (`targets`, read each frame: a text's lines come and go)
 * seen through the shape `shape` -- or with it cut out (`hole`). null/"off"
 * takes it away, and what it masked is whole again.
 */
export function setMask(key, targets, shape, hole = false) {
  const was = masks.get(key);
  if (was) {
    for (const el of was.targets()) unmaskEl(el);
    // a text that was the window: seen again, unless another still uses it
    if (![...masks.entries()].some(([k, m]) => k !== key && m.shape === was.shape)) {
      for (const el of textEls(was.shape)) if (el.style.visibility === "hidden") el.style.visibility = el._oscillaWasVisibility ?? "";
    }
  }
  const off = shape == null || /^(off|none|0)$/i.test(String(shape));
  if (off) { masks.delete(key); return; }
  masks.set(key, { targets, shape: String(shape), hole: !!hole, goneSince: null });
  if (!raf) raf = requestAnimationFrame(frame);
}

/** mask: / hole: from a cue's arguments, for `key`: whether either was given. */
export function maskFromArgs(key, targets, arg) {
  const m = arg("mask"), h = arg("hole");
  if (m == null && h == null) return false;
  setMask(key, targets, m ?? h, m == null);
  return true;
}

/** Every mask taken away (still(all), a new example). */
export function clearMasks() {
  for (const key of [...masks.keys()]) setMask(key, () => [], null);
}
onReset("stage", clearMasks);
